import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { closeSnackbar, SnackbarProvider } from "notistack";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import "./App.scss";
import Auth0ProviderWithNavigate from "./Auth0ProviderWithNavigate.jsx";
import AppRoutes from "./routes/index.jsx";
import SilentAuth from "./SilentAuth";
import store from "./store/index.js";

import { forwardRef } from "react";
import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// Custom Success Toast Component with ref forwarding
const SuccessToast = forwardRef((props, ref) => {
  const { message } = props;
  return (
    <div 
      ref={ref}
      className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-[500px]"
    >
      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
      <span className="flex-1">{message}</span>
    </div>
  );
});

SuccessToast.displayName = "SuccessToast";

// Custom Error Toast Component with ref forwarding
const ErrorToast = forwardRef((props, ref) => {
  const { message } = props;
  return (
    <div 
      ref={ref}
      className="bg-red-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px] max-w-[500px]"
    >
      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
      <span className="flex-1">{message}</span>
    </div>
  );
});

ErrorToast.displayName = "ErrorToast";

const AppAnalytics = () => {
  const { user, isAuthenticated } = useAuth0();
  useEffect(() => {
    const isProductionDomain = window.location.hostname === "au.wowzerbackupandrestore.com";
    if (isProductionDomain && isAuthenticated && user && window.clarity) {
      window.clarity("set", "userId", user.email || user.sub );
    }
  }, [isAuthenticated, user]);

  return null;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <SnackbarProvider
        maxSnack={3}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        preventDuplicate
        Components={{
          success: SuccessToast,
          error: ErrorToast,
        }}
      >
        <BrowserRouter>
          <Auth0ProviderWithNavigate>
            <AppAnalytics />
            <SilentAuth />
            <Provider store={store}>
              <AppRoutes />
            </Provider>
            {/* <Feedback /> */}
          </Auth0ProviderWithNavigate>
        </BrowserRouter>
      </SnackbarProvider>
    </QueryClientProvider>
  );
};

export default App;
